<template>
    <div>
        <div>
            <q-form class="row q-col-gutter-sm">
                <div class="col-12 text-h6">{{$t('users.personal_info')}}</div>
                <div class="col-xs-12" :class="isMiddleNameFree ? 'col-md-6' : 'col-md-4'">
                    <q-input
                        v-model="kycObj.first_name"
                        :label="$t('users.table_titles.first_name')"
                        color="primary"
                        dense
                        lazy-rules
                        :rules="[ val => val && val.length > 0 || $t('validations.is_required', {field_name: $t('users.table_titles.first_name')})]"
                        :error="kycObj.errors.has('first_name')"
                        :error-message="kycObj.errors.get('first_name')"
                        v-english-only
                        dir="ltr"
                    />
                </div> <!-- First name -->
                <div class="col-md-4 col-xs-12" v-show="!isMiddleNameFree">
                    <q-input
                        v-model="kycObj.middle_name"
                        :label="$t('users.table_titles.middle_name')"
                        color="primary"
                        dense
                        hint=""
                        v-english-only
                        dir="ltr"
                    />
                </div> <!-- Middle name -->
                <div class="col-xs-12" :class="isMiddleNameFree ? 'col-md-6' : 'col-md-4'">
                    <q-input
                        v-model="kycObj.last_name"
                        :label="$t('users.table_titles.last_name')"
                        color="primary"
                        dense
                        lazy-rules
                        :rules="[ val => val && val.length > 0 || $t('validations.is_required', {field_name: $t('users.table_titles.last_name')})]"
                        :error="kycObj.errors.has('last_name')"
                        :error-message="kycObj.errors.get('last_name')"
                        v-english-only
                        dir="ltr"
                    />
                </div> <!-- Last name -->
                <div class="col-md-4 col-xs-12">
                    <q-select
                        :label="$t('users.table_titles.gender')"
                        v-model="kycObj.gender"
                        :options="genders"
                        emit-value
                        map-options
                        clearable
                        option-label="label"
                        option-value="value"
                        dense
                    />
                </div> <!-- Gender -->
                <div class="col-md-4 col-xs-12">
                    <q-input
                        :label="$t('users.table_titles.date_of_birth')"
                        v-model="kycObj.date_of_birth"
                        mask="date"
                        dense
                        :error="kycObj.errors.has('date_of_birth')"
                        :error-message="kycObj.errors.get('date_of_birth')"
                        lazy-rules
                        :rules="[ val => val && val.length > 0 || $t('validations.is_required', {field_name: $t('users.table_titles.date_of_birth')})]"
                        placeholder="YYYY/MM/DD"
                        hint="YYYY/MM/DD"
                        color="primary"
                        dir="ltr"
                        :class="{'fix-template': $q.lang.rtl}"
                    >
                        <template v-slot:prepend>
                            <q-icon name="fa fa-calendar-alt" class="cursor-pointer">
                                <q-popup-proxy ref="qDateProxyDateOfBirth" transition-show="scale" transition-hide="scale">
                                    <q-date
                                        v-model="kycObj.date_of_birth"
                                        @input="() => $refs.qDateProxyDateOfBirth.hide()"
                                        color="primary"
                                    />
                                </q-popup-proxy>
                            </q-icon>
                        </template>
                    </q-input>
                </div> <!-- Date of birth -->
                <div class="col-md-4 col-xs-12">
                    <q-input
                        v-model="mobile"
                        :label="$t('users.table_titles.mobile')"
                        mask="###########"
                        color="primary"
                        clearable
                        lazy-rules
                        dense
                        :rules="[ val => val && val.length > 0 || $t('validations.is_required', {field_name: $t('users.table_titles.mobile')})]"
                        :error="kycObj.errors.has('mobile')"
                        :error-message="kycObj.errors.get('mobile')"
                        v-english-only
                        dir="ltr"
                        :class="{'fix-template': $q.lang.rtl}"
                    >
                        <template v-slot:prepend>
                            <small>+{{ selectedCountry.calling_code }}</small>
                        </template>
                    </q-input>
                </div> <!-- Mobile -->
                <div class="col-12">
                    <q-select
                        :label="$t('users.address')"
                        :value="kycObj.address"
                        use-input
                        hide-selected
                        fill-input
                        dense
                        input-debounce="0"
                        :options="addresses"
                        @filter="filterAddresses"
                        @input-value="setAddress"
                        hide-dropdown-icon
                        :placeholder="isAddressSearchable ? $t('start_typing_the_address') : ''"
                        lazy-rules
                        :rules="[ val => val && val.length > 0 || $t('validations.is_required', {field_name: $t('users.address')})]"
                        :error="kycObj.errors.has('address')"
                        :error-message="kycObj.errors.get('address')"
                        color="primary"
                    />
                </div> <!-- Address -->
                <div class="col-12 text-h6">{{$t('id_document')}}</div>
                <div class="col-md-6 col-xs-12">
                    <q-select
                        v-model="kycObj.type"
                        :options="types"
                        :label="$t('users.id_document_type')"
                        emit-value
                        map-options
                        dense
                        color="primary"
                        :error="kycObj.errors.has('type')"
                        :error-message="kycObj.errors.get('type')"
                    />
                </div> <!-- Type -->
                <div class="col-md-6 col-xs-12">
                    <q-select
                        v-model="kycObj.country"
                        :label="$t('users.country_of_id_document')"
                        emit-value
                        map-options
                        dense
                        option-value="acronym3"
                        option-label="name"
                        use-input
                        :options="options"
                        :loading="loading"
                        :error="kycObj.errors.has('country')"
                        :error-message="kycObj.errors.get('country')"
                        :rules="[ val => val && val.length > 0 || $t('validations.is_required', {field_name: $t('users.country_of_id_document')})]"
                        color="primary"
                        @filter="filterCountries"
                    >
                        <template v-slot:option="scope">
                            <q-item
                                v-bind="scope.itemProps"
                                v-on="scope.itemEvents"
                            >
                                <q-item-section avatar>
                                    <q-icon :name="scope.opt.acronym" size="sm" />
                                </q-item-section>
                                <q-item-section>
                                    <q-item-label>{{scope.opt.name}}</q-item-label>
                                </q-item-section>
                            </q-item>
                        </template>
                        <template v-slot:after="scope">
                            <q-btn round outline icon="fa fa-question" size="sm" color="blue" @click="displayCountryHelpDialog = true"/>
                        </template>
                    </q-select>
                </div> <!-- Country -->
                <div class="col-md-6 col-xs-12">
                    <q-input
                        v-model="kycObj.id_number"
                        :label="id_number_label"
                        lazy-rules
                        color="primary"
                        dense
                        :rules="[ val => val && val.length > 0 || $t('validations.is_required', {field_name: id_number_label})]"
                        :error="kycObj.errors.has('id_number')"
                        :error-message="kycObj.errors.get('id_number')"
                        :key="kycObj.type"
                        v-english-only
                        dir="ltr"
                        :class="{'fix-template': $q.lang.rtl}"
                    />
                </div> <!-- Id number -->
                <div class="col-md-6 col-xs-12">
                    <q-input
                        v-model="kycObj.expiry_date"
                        :label="$t('users.expiry_date')"
                        mask="date"
                        dense
                        :error="kycObj.errors.has('expiry_date')"
                        :error-message="kycObj.errors.get('expiry_date')"
                        lazy-rules
                        :rules="[ val => val && val.length > 0 || $t('validations.is_required', {field_name: $t('users.expiry_date')})]"
                        placeholder="YYYY/MM/DD"
                        hint="YYYY/MM/DD"
                        color="primary"
                        dir="ltr"
                        :class="{'fix-template': $q.lang.rtl}"
                    >
                        <template v-slot:prepend>
                            <q-icon name="fa fa-calendar-xmark" class="cursor-pointer">
                                <q-popup-proxy ref="qDateProxyExpiredDate" transition-show="scale" transition-hide="scale">
                                    <q-date
                                        v-model="kycObj.expiry_date"
                                        @input="() => $refs.qDateProxyExpiredDate.hide()"
                                        color="primary"
                                    />
                                </q-popup-proxy>
                            </q-icon>
                        </template>
                    </q-input>
                </div> <!-- Expiry Date -->
                <div v-html="verificationType.instructions" class="col-12 q-my-md"></div>

                <div class="col-12">
                    <file-collector
                        v-model="kycObj.documents"
                        multiple
                        class="col-12"
                        :label="$t('documents')"
                        name="documents"
                        :attach-file-label="$t('attach_file')"
                        ref="collector"
                        camera
                        @count="fileCounts"
                        file-types="*"
                        :max-file-size="25 * 1024 * 1024"
                        :max-total-size="40 * 1024 * 1024"
                        :error="kycObj.errors.get('documents', true)"
                        :cameraLabel="$t('take_photo')"
                        :of="$t('of')"
                    />
                </div>

                <div class="col-12 text-right">
                    <q-btn color="primary"
                           @click="submit"
                           :disabled="!allFieldsFilled"
                           :loading="loading"
                           :style="loading ? 'width: 200px':''"
                           class="q-mt-sm"
                    >{{$t('submit')}}
                        <template v-slot:loading>
                            <div class="col">
                                <q-spinner-radio class="on-left"/>
                                {{$t('submitting')}} ...
                            </div>
                        </template>
                    </q-btn>
                </div> <!-- Submit button -->
            </q-form>
            <q-inner-loading :showing="loading_form">
                <q-spinner-gears size="50px" color="primary" />
            </q-inner-loading>
        </div>

        <q-dialog v-model="is_submitted">
            <alert-user-dialog>
                <template v-slot:title>
                    <div class="text-h6">{{$t('users.information_submitted')}}</div>
                </template>
                <template v-slot:message>
                    <div v-html="verificationType.dialog_content"></div>
                </template>
            </alert-user-dialog>
        </q-dialog>
        <q-dialog v-model="displayCountryHelpDialog">
            <q-card class="bg-primary text-white">
                <q-card-section>
                    <p>{{$t('users.select_new_zealand_or_australia_for_nz_and_aus_driver_s_licence_or_passport')}}</p>
                    <p>{{$t('users.select_iran_if_you_re_sending_an_iranian_passport_as_your_id_document')}}</p>
                </q-card-section>

                <q-card-actions align="right" class="bg-white text-primary">
                    <q-btn flat :label="$t('ok')" v-close-popup />
                </q-card-actions>
            </q-card>
        </q-dialog>
    </div>
</template>
<script>

import Kyc from "Modules/User/resources/models/Kyc"
import AlertUserDialog from "../../AlertUserDialog"
import FileCollector from "src/components/FileCollector.vue"
import {mapActions, mapGetters} from 'vuex'
import User from "../../../../models/User"
export default {
    name: "ProfileDetailsForm",
    props: {
        selectedCountry: {
            type: Object,
            required: true
        },
        verificationType: {
            type: Object,
            required: true
        },
    },
    computed: {
        ...mapGetters(['id_valid_countries', 'currentUser']),
        allFieldsFilled () {
            return this.kycObj.first_name
                && this.kycObj.last_name
                && this.mobile
                && this.kycObj.date_of_birth
                && this.kycObj.address
                && this.kycObj.country
                && this.kycObj.type
                && this.kycObj.id_number
                && this.kycObj.expiry_date
                && this.count_of_files > 0
        },
        isMiddleNameFree() {
            return ['IR'].includes(this.selectedCountry.acronym) || ['IRN'].includes(this.kycObj.country)
        },
        isAddressSearchable() {
            return ['NZ', 'AU'].includes(this.selectedCountry.acronym)
        },
        id_number_label() {
            switch (this.kycObj.type) {
                case 'passport':
                    return this.$t('users.passport_number')
                case 'id_card':
                    return this.$t('users.id_number')
                default:
                    return this.$t('users.licence_number')
            }
        }
    },
    data() {
        return {
            loading: false,
            loading_form: false,
            mobile: undefined,
            country_id: this.selectedCountry.id,
            count_of_files: 0,
            kycObj: new Kyc({
                country_id: undefined,
                account_type: 'personal',
                mobile: undefined,
                first_name: '',
                middle_name: '',
                last_name: '',
                gender: null,
                date_of_birth: undefined,
                address: '',
                country: undefined,
                type: 'passport',
                id_number: undefined,
                expiry_date: undefined,
                documents: {},
            }),
            genders: [
                {label: this.$t('male'), value: 'M', icon: 'fa fa-male', color: 'blue'},
                {label: this.$t('female'), value: 'F', icon: 'fa fa-female', color: 'pink'},
                {label: this.$t('other'), value: 'O', icon: 'fa fa-genderless', color: 'purple'},
            ],
            displayGenders: false,
            success_message: '',
            is_submitted: false,
            types: [
                {label: this.$t('users.id_document_type_labels.driver_licence'), value: 'driver_licence'},
                {label: this.$t('users.id_document_type_labels.passport'), value: 'passport'},
                {label: this.$t('users.id_document_type_labels.identification_card'), value: 'id_card'},
            ],
            options: null,
            displayCountryHelpDialog: false,
            addresses: [],
        }
    },
    methods: {
        ...mapActions(['set_countries']),
        submit() {
            this.loading = true
            this.kycObj.mobile = this.mobile
            this.kycObj.country_id = this.country_id
            this.kycObj.register((message) => {
                this.is_submitted = true
                this.success_message = message
                this.$refs.collector.reset()
                this.loading = false
            }).catch((response) => {
                switch (response.status) {
                    case 412:
                        this.$q.notify({
                            message: response.data.error,
                            color: 'negative',
                            position: 'center',
                            icon: 'fa fa-exclamation-triangle'
                        })
                        break
                }
                this.loading = false
                window.scrollTo(0, 0)
            })
        },
        fileCounts(count) {
            this.count_of_files = count
        },
        filterCountries(val, update, abort) {
            if (val === '') {
                update(() => {
                    this.options = this.id_valid_countries
                })
                return
            }

            update(() => {
                const needle = val.toLowerCase()
                this.options = this.id_valid_countries.filter(country => country.name.toLowerCase().indexOf(needle) > -1)
            })
        },
        filterAddresses(val, update, abort) {
            if (val.length < 5 || !this.selectedCountry || !this.isAddressSearchable) {
                abort()
                return
            }

            User.addresses(`q=${val}&c=${this.selectedCountry.acronym}`).then(response => {
                this.addressOptions = response
                update(() => {
                    this.addresses = this.addressOptions
                })
            }).catch(data => {
                abort()
            })
        },
        setAddress(address) {
            this.kycObj.address = address
        },
        fetchFormData() {
            this.loading_form = true
            this.kycObj.fetch((data) => {
                this.mobile = data.mobile
                this.loading_form = false
            })
        }
    },
    watch: {
        is_submitted(val, old) {
            if (old && !val) {
                this.$emit('reloadUser')
            }
        },
    },
    mounted() {
        if (! this.id_valid_countries.length) {
            this.loading = true
            this.set_countries().then(() => {this.loading = false}).catch(() => {this.loading = false})
        }
        this.fetchFormData()
    },
    components: {
        FileCollector,
        AlertUserDialog
    },
}
</script>
<style>

</style>
